import { useEffect, useState } from 'react'
import {
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Input,
  HStack,
  VStack,
  Box,
  Button
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import { useStores } from 'hooks/useStores'
import compact from 'lodash/compact'
import get from 'lodash/get'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import colors from 'theme/colors.theme'
import { UpdateBody } from 'types/common'
import { createManyMedias, dropMediaType } from 'API/media'
import { getRenderProcess } from 'components/Pages/ProcessDetailPage/utils'
import PrimaryButton from 'components/PrimaryButton'
import SvgIcon from 'components/SvgIcon'
import {
  DARK_INPUT_SX,
  DARK_MODAL_CANCEL_BUTTON,
  DARK_MODAL_CONTENT,
  DARK_MODAL_OVERLAY,
  DARK_SURFACE_GRADIENT,
  DARK_TABLE_BORDER,
  DARK_TEXT_LIGHT,
  DARK_TEXT_MUTED,
  DARK_TEXT_PRIMARY
} from 'constants/dark-tokens'
import { MediaType } from 'constants/media'
import { StepDefaultId } from 'constants/processStep'
import { IMedia } from 'interfaces/media'
import { IStepWithRelations } from 'interfaces/step'
import { getValidArray } from 'utils/common'
import CustomButton from '../../../CustomButton'
import { MediaTextFormValues } from '../../enums'

interface IEmbedLinkModalProps {
  isOpen: boolean
  step?: IStepWithRelations
  mediaList: IMedia[]
  /** Dark RallyPD chrome, for the Stack Detail media picker. */
  isDark?: boolean
  /**
   * Owning organization. Stack Detail does not load `organizationStore`, so the
   * store fallback would write embed links with no organizationId there.
   */
  organizationId?: number
  onClose: () => void
}

const EmbedLinkModal = (props: IEmbedLinkModalProps) => {
  const { isOpen, onClose, step, mediaList, isDark = false, organizationId } = props
  const { organizationStore, processStore } = useStores()
  const { control, register, getValues } = useForm()
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: MediaTextFormValues.EMBED_LINK
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const savedLinks = compact(
    getValidArray(mediaList).map(media => (media?.mediaType === MediaType.EMBED_LINK ? { content: media.url } : null))
  )
  // Keyed on the saved urls rather than the array identity: `mediaList` is a new
  // array on every render, so depending on it would re-seed the rows under the
  // cursor while a link is being typed.
  const savedSignature = savedLinks.map(link => link.content).join('\u0000')

  function seedRows(): void {
    // `replace` rather than `reset`: the field array owns the rows, and resetting
    // the form left it holding the previous open's rows, which is why reopening
    // showed nothing after links had been saved.
    // The trailing empty row keeps the modal typeable the moment it opens.
    replace([...savedLinks, { content: '' }])
  }
  async function handleSave(): Promise<void> {
    setIsLoading(true)
    try {
      await dropMediaType(MediaType.EMBED_LINK, step?.id ?? StepDefaultId.NONE)
      const listEmbedLinkMedia: UpdateBody<IMedia>[] = compact(
        getValidArray(getValues(MediaTextFormValues.EMBED_LINK)).map(field => {
          // The trailing row is empty until it is typed into; saving it would
          // create a media row with no url.
          if (step?.id && String(get(field, 'content') ?? '').trim()) {
            return {
              organizationId: organizationId ?? organizationStore.organization?.id,
              stepId: step.id,
              mediaType: MediaType.EMBED_LINK,
              url: get(field, 'content'),
              createdAt: dayjs().toDate(),
              updatedAt: dayjs().toDate()
            }
          }
          return null
        })
      )
      await createManyMedias(listEmbedLinkMedia)
      step?.processId && getRenderProcess(step.processId, processStore)
      toast.success('Embed link saved successfully')
    } catch (error: any) {
      toast.error(error?.message ?? 'Something went wrong')
    }
    setIsLoading(false)
    onClose()
  }

  useEffect(() => {
    if (isOpen) seedRows()
  }, [isOpen, savedSignature])

  return (
    <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay {...(isDark ? DARK_MODAL_OVERLAY : {})} />
      <ModalContent
        minWidth={{ base: 'auto', lg: '600px' }}
        {...(isDark ? DARK_MODAL_CONTENT : {})}
        borderRadius={isDark ? '24px' : undefined}
        border={isDark ? 'none' : undefined}
        bg={isDark ? DARK_SURFACE_GRADIENT : undefined}
      >
        <ModalHeader fontSize="lg" fontWeight={500} lineHeight={7} color={isDark ? DARK_TEXT_PRIMARY : 'gray.800'}>
          {isDark ? 'Embed links' : 'Embed link'}
        </ModalHeader>
        <Divider borderColor={isDark ? DARK_TABLE_BORDER : 'gray.200'} margin={0} />
        <ModalCloseButton background={isDark ? 'transparent' : 'white'} border="none" boxShadow="none !important" />
        <ModalBody padding={6}>
          <VStack spacing={4} alignItems="flex-start">
            {!isDark && (
              <Text fontSize="md" lineHeight={6} fontWeight={500} color="gray.700" margin={0}>
                Link(s)
              </Text>
            )}
            {getValidArray(fields).map((field, index: number) => {
              return (
                <HStack key={`field-${field.id}`} justifyContent="space-between" width="full">
                  <Input
                    {...register(`${MediaTextFormValues.EMBED_LINK}.${index}.content`)}
                    placeholder="Enter link"
                    backgroundColor={isDark ? undefined : 'white'}
                    borderRadius="6px"
                    sx={isDark ? DARK_INPUT_SX : undefined}
                  />
                  <Box width={10} height={10} padding="10px" cursor="pointer" onClick={() => remove(index)}>
                    <SvgIcon iconName="ic_close" color={isDark ? DARK_TEXT_MUTED : colors.gray[600]} size={12} />
                  </Box>
                </HStack>
              )
            })}
            <Text
              fontSize="sm"
              lineHeight={5}
              fontWeight={500}
              color={isDark ? DARK_TEXT_LIGHT : 'gray.600'}
              margin={0}
              alignSelf={isDark ? 'center' : 'flex-start'}
              cursor="pointer"
              onClick={() => append({})}
            >
              + Add new link
            </Text>
          </VStack>
        </ModalBody>
        <Divider borderColor={isDark ? DARK_TABLE_BORDER : 'gray.200'} margin={0} />
        <ModalFooter gap={isDark ? 3 : 0} paddingY={isDark ? '16px' : undefined}>
          {isDark ? (
            <>
              <Button {...DARK_MODAL_CANCEL_BUTTON} isDisabled={isLoading} onClick={onClose}>
                Cancel
              </Button>
              <PrimaryButton isLoading={isLoading} onClick={handleSave}>
                Save
              </PrimaryButton>
            </>
          ) : (
            <>
              <CustomButton content="Cancel" className="outline" onClick={onClose} isLoading={isLoading} />
              <CustomButton content="Save" className="primary" isLoading={isLoading} onClick={handleSave} />
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default EmbedLinkModal
